import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { DataTable } from "./DataTable";
import { ExportButtons } from "./ExportButtons";
import { supabase } from "../../lib/supabase";
import { staffDirectory, fmtDateTime } from "../../lib/adminApi";

const RESP = { interested: "bg-emerald-50 text-emerald-800", not_interested: "bg-red-50 text-red-800", none: "bg-stone-100 text-stone-600" };
const respLabel = (r) => ({ interested: "Interested", not_interested: "Not interested", none: "No response" }[r]);

export function ShareHistory({ clientId, reloadKey, onChanged, share }) {
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState({});
  const [resp, setResp] = useState("");
  const load = useCallback(async () => {
    let q = supabase.from("shared_profiles")
      .select("*, shared:profiles!shared_profiles_shared_profile_id_fkey(id, full_name, registration_no)")
      .eq("client_profile_id", clientId).order("shared_at", { ascending: false });
    if (resp) q = q.eq("response", resp);
    const { data, error } = await q;
    if (error) toast.error(error.message); else setRows(data);
  }, [clientId, resp]);
  useEffect(() => { load(); staffDirectory().then(setStaff); }, [load, reloadKey]);

  const revoke = async (r) => {
    const { error } = await supabase.from("shared_profiles").update({ is_revoked: true, revoked_at: new Date().toISOString() }).eq("id", r.id);
    if (error) return toast.error(error.message);
    toast.success("Share revoked. The client no longer sees this profile.");
    load(); onChanged();
  };

  const columns = [
    { key: "profile", label: "Profile", render: (r) => <Link to={`/admin/profiles/${r.shared.id}`} className="font-semibold text-[#7A0016] hover:underline">{r.shared.full_name} · {r.shared.registration_no}</Link> },
    { key: "shared_at", label: "Shared", render: (r) => fmtDateTime(r.shared_at) },
    { key: "by", label: "Shared by", render: (r) => staff[r.shared_by] || "-" },
    { key: "response", label: "Client response", render: (r) => <span data-testid={`share-response-${r.id}`} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${RESP[r.response]}`}>{respLabel(r.response)}</span> },
    { key: "state", label: "Access", render: (r) => (r.is_revoked ? <span className="text-xs font-semibold text-[#A31828]">Revoked {fmtDateTime(r.revoked_at)}</span> : <span className="text-xs font-semibold text-emerald-700">Active</span>) },
  ];
  const exportCols = [
    { key: "p", label: "Profile", value: (r) => `${r.shared.full_name} (${r.shared.registration_no})` },
    { key: "s", label: "Shared at", value: (r) => fmtDateTime(r.shared_at) },
    { key: "b", label: "Shared by", value: (r) => staff[r.shared_by] || "" },
    { key: "r", label: "Response", value: (r) => respLabel(r.response) },
    { key: "a", label: "Access", value: (r) => (r.is_revoked ? "Revoked" : "Active") },
  ];
  return (
    <div className="space-y-3" data-testid="share-history">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <select value={resp} onChange={(e) => setResp(e.target.value)} data-testid="history-response-filter" className="rmm-select rmm-input h-9 w-48">
          <option value="">All responses</option>
          <option value="interested">Interested</option>
          <option value="not_interested">Not interested</option>
          <option value="none">No response</option>
        </select>
        <ExportButtons fetchRows={async () => rows} columns={exportCols} name="share-history" title="Share history" />
      </div>
      <DataTable columns={columns} rows={rows} sort={{}} rowActions={(r) => (r.is_revoked
        ? <button type="button" onClick={() => share([r.shared_profile_id])} data-testid={`reshare-${r.id}`} className="text-xs font-semibold text-[#C59B27] hover:text-[#7A0016]">Share again</button>
        : <button type="button" onClick={() => revoke(r)} data-testid={`revoke-${r.id}`} className="text-xs font-semibold text-[#A31828] hover:underline">Revoke</button>)} />
    </div>
  );
}
